import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Bot, Send, User, Clipboard, Check, RotateCcw, MapPin, Search,
  Info, Users, Car, UserSearch, HeartPulse, Calendar, CloudRain,
  Siren, Home, Droplet, Sparkles, HeartHandshake, Banknote,
  PlayCircle, StopCircle,
} from "lucide-react";
import { usePreferences } from "../context/PreferencesContext";
import { googleColor } from "../data/googleColors";

// ── Bilingual UI chrome ──────────────────────────────────────────
const COPY = {
  en: {
    badge: "Government of Maharashtra · Simhastha Kumbh 2027",
    title: "Kumbh Sathi",
    subtitle: "Your AI guide to crowd status, transport, medical camps, lost & found and everything else you need to know — in English or Marathi.",
    statQueries: "Queries Today", statTopics: "Topics Covered", statZones: "Ghats & Zones", statLangs: "Languages",
    newChat: "New Chat",
    tabTopics: "Quick Topics", tabZones: "Ghats & Zones",
    disclaimer: "Demonstration console. All answers are illustrative and generated from static sample data — a human officer must verify before any operational decision.",
    chatHeader: "Kumbh Sathi", chatSub: "AI Pilgrim & Command Assistant",
    poweredBy: "Powered by Kumbh AI",
    inputPlaceholder: "Ask about crowd status, transport, medical camps, lost & found…",
    thinking: "Looking that up…",
    sources: "Sources", askFollowup: "You might also ask",
    copy: "Copy", copied: "Copied",
    zoneBriefing: (z) => `Give me a status briefing for ${z}`,
    runDemo: "Run Collector Demo", stopDemo: "Stop Demo",
    demoLive: "Demo running",
    demoProgress: (n, total) => `Question ${n} of ${total}`,
  },
  mr: {
    badge: "महाराष्ट्र शासन · सिंहस्थ कुंभ 2027",
    title: "कुंभ साथी",
    subtitle: "गर्दीची स्थिती, वाहतूक, वैद्यकीय शिबिरे, हरवलेल्या व्यक्ती आणि आवश्यक असलेली प्रत्येक गोष्ट — इंग्रजी किंवा मराठीत विचारा.",
    statQueries: "आजचे प्रश्न", statTopics: "विषय समाविष्ट", statZones: "घाट व झोन", statLangs: "भाषा",
    newChat: "नवीन संभाषण",
    tabTopics: "जलद विषय", tabZones: "घाट व झोन",
    disclaimer: "प्रात्यक्षिक कन्सोल. सर्व उत्तरे उदाहरणादाखल असून स्थिर नमुना डेटावरून तयार केली आहेत — कोणत्याही कारवाईपूर्वी मानवी अधिकाऱ्याने पडताळणी करणे आवश्यक आहे.",
    chatHeader: "कुंभ साथी", chatSub: "एआय यात्रेकरू व कमांड सहाय्यक",
    poweredBy: "कुंभ एआय द्वारा समर्थित",
    inputPlaceholder: "गर्दीची स्थिती, वाहतूक, वैद्यकीय शिबिरे, हरवलेल्या व्यक्तींबद्दल विचारा…",
    thinking: "शोधत आहे…",
    sources: "स्रोत", askFollowup: "आपण हे देखील विचारू शकता",
    copy: "कॉपी करा", copied: "कॉपी झाले",
    zoneBriefing: (z) => `${z} साठी सद्यस्थिती द्या`,
    runDemo: "जिल्हाधिकारी प्रात्यक्षिक सुरू करा", stopDemo: "प्रात्यक्षिक थांबवा",
    demoLive: "प्रात्यक्षिक सुरू आहे",
    demoProgress: (n, total) => `प्रश्न ${n} / ${total}`,
  },
};

// ── Quick-topic prompts (grid buttons on the left panel) ─────────
const TOPICS = [
  { id: "crowd",         icon: Users,      en: "Crowd & Ghat Status",      mr: "गर्दी व घाट स्थिती",          prompt: { en: "What's the current crowd status across the ghats?", mr: "घाटांवरील सध्याची गर्दीची स्थिती काय आहे?" } },
  { id: "transport",     icon: Car,        en: "Transport & Parking",      mr: "वाहतूक व पार्किंग",           prompt: { en: "Tell me about transport and parking options.", mr: "वाहतूक व पार्किंगबद्दल सांगा." } },
  { id: "lostfound",     icon: UserSearch, en: "Lost & Found",             mr: "हरवले व सापडले",             prompt: { en: "How do I report or find a missing person?", mr: "हरवलेल्या व्यक्तीची तक्रार कशी करावी?" } },
  { id: "medical",       icon: HeartPulse, en: "Medical & Health Camps",   mr: "वैद्यकीय व आरोग्य शिबिरे",    prompt: { en: "Where are the nearest medical camps?", mr: "जवळचे वैद्यकीय शिबिर कुठे आहे?" } },
  { id: "schedule",      icon: Calendar,   en: "Shahi Snan Schedule",      mr: "शाही स्नान वेळापत्रक",        prompt: { en: "What's the Shahi Snan and Akhara schedule?", mr: "शाही स्नान व आखाडा वेळापत्रक काय आहे?" } },
  { id: "weather",       icon: CloudRain,  en: "Weather & River Level",    mr: "हवामान व नदी पातळी",          prompt: { en: "What's today's weather and Godavari river level?", mr: "आजचे हवामान व गोदावरी नदीची पातळी काय आहे?" } },
  { id: "safety",        icon: Siren,      en: "Safety & Emergency",       mr: "सुरक्षा व आपत्कालीन",         prompt: { en: "What are the emergency helpline numbers?", mr: "आपत्कालीन हेल्पलाइन क्रमांक काय आहेत?" } },
  { id: "accommodation", icon: Home,       en: "Accommodation & Stay",     mr: "निवास व राहण्याची सोय",       prompt: { en: "Where can pilgrims find free camps or a dharamshala to stay?", mr: "यात्रेकरूंसाठी मोफत निवास किंवा धर्मशाळा कुठे आहे?" } },
  { id: "facilities",    icon: Droplet,    en: "Toilets, Water & Facilities", mr: "शौचालय, पाणी व सुविधा",   prompt: { en: "Where are the nearest toilets and drinking water points?", mr: "जवळचे शौचालय व पिण्याचे पाणी कुठे आहे?" } },
  { id: "volunteer",     icon: HeartHandshake, en: "Volunteering & Donations", mr: "स्वयंसेवा व देणगी",       prompt: { en: "How can I volunteer or donate during the Kumbh?", mr: "कुंभ मेळ्यादरम्यान मी स्वयंसेवा किंवा देणगी कशी देऊ शकतो?" } },
  { id: "atm",           icon: Banknote,   en: "ATMs & Cash Points",        mr: "एटीएम व रोकड केंद्रे",        prompt: { en: "Where can I find an ATM or cash point nearby?", mr: "जवळपास एटीएम किंवा रोकड केंद्र कुठे आहे?" } },
];

const ZONES = ["Ramkund Ghat", "Panchvati", "Trimbakeshwar", "Nashik Road Stn", "Kalaram Temple", "Gangapur Camp"];
const ZONES_MR = { "Ramkund Ghat": "रामकुंड घाट", "Panchvati": "पंचवटी", "Trimbakeshwar": "त्र्यंबकेश्वर", "Nashik Road Stn": "नाशिक रोड स्थानक", "Kalaram Temple": "काळाराम मंदिर", "Gangapur Camp": "गंगापूर कॅम्प" };

// ── Collector demo script — 10 of the 11 topics, ordered for a live
// walkthrough (command-relevant capability first, closes on volunteering).
// "atm" is left out — least relevant to a command-level demo audience.
const DEMO_SCRIPT = ["crowd", "safety", "medical", "lostfound", "transport", "schedule", "weather", "accommodation", "facilities", "volunteer"];

// ── Canned knowledge base — no API, this is the entire "AI". A regex router
// below matches the user's question to one of these topics and a fake
// thinking delay (900–1800ms) stands in for real latency. ──
const RESPONSES = {
  crowd: {
    en: `**Live Crowd Snapshot — Nashik Ghats**

Density across the six primary ghats, from CCTV and foot-traffic sensors.

| Ghat | Density | Status |
| Ramkund Ghat | 92% | 🔴 Critical |
| Panchvati | 81% | 🟡 High |
| Trimbakeshwar | 76% | 🟡 High |
| Nashik Road Stn | 68% | 🟢 Normal |
| Kalaram Temple | 58% | 🟢 Normal |
| Gangapur Camp | 41% | 🟢 Normal |

**Priority Actions**
1. Divert Ramkund Entry 2 traffic to Entry 4 — overflow shelter OFZ-B is active.
2. Panchvati is trending up ahead of the 15:30 CM visit — pre-position extra barricades.
3. Trimbakeshwar is steady — routine monitoring only.

⚠️ Ramkund Ghat has stayed above 90% for 22 minutes. Escalate if it doesn't ease within the hour.`,
    mr: `**थेट गर्दी स्नॅपशॉट — नाशिक घाट**

सहा प्रमुख घाटांवरील घनता, सीसीटीव्ही व पदपथ सेन्सर्समधून.

| घाट | घनता | स्थिती |
| रामकुंड घाट | 92% | 🔴 गंभीर |
| पंचवटी | 81% | 🟡 उच्च |
| त्र्यंबकेश्वर | 76% | 🟡 उच्च |
| नाशिक रोड स्थानक | 68% | 🟢 सामान्य |
| काळाराम मंदिर | 58% | 🟢 सामान्य |
| गंगापूर कॅम्प | 41% | 🟢 सामान्य |

**प्राधान्य कृती**
1. रामकुंड प्रवेशद्वार 2 वरील गर्दी प्रवेशद्वार 4 कडे वळवा — अतिप्रवाह निवारा OFZ-B सक्रिय आहे.
2. मुख्यमंत्र्यांच्या 15:30 भेटीपूर्वी पंचवटीतील गर्दी वाढत आहे — अतिरिक्त बॅरिकेड्स लावा.
3. त्र्यंबकेश्वर स्थिर आहे — केवळ नियमित निरीक्षण आवश्यक.

⚠️ रामकुंड घाट गेल्या 22 मिनिटांपासून 90% पेक्षा जास्त आहे. एका तासात न कमी झाल्यास वाढवा.`,
    sources: ["Live CCTV Feed", "Crowd Risk Engine", "Foot-traffic Sensors"],
    followups: [
      { id: "crowd-diversion-plan",  en: "What's the diversion plan for Ramkund Ghat?", mr: "रामकुंड घाटासाठी वळवण्याची योजना काय आहे?" },
      { id: "crowd-shravan-trend",   en: "What's the crowd density trend for Shravan Purnima?", mr: "श्रावण पौर्णिमेसाठी गर्दी घनतेचा कल काय आहे?" },
      { id: "crowd-barricades-today",en: "Which ghats need extra barricades today?", mr: "आज कोणत्या घाटांवर अतिरिक्त बॅरिकेड्स हवेत?" },
    ],
  },
  transport: {
    en: `**Getting Around Nashik & Trimbakeshwar**

**By bus** — MSRTC shuttles run every 10–15 min between Nashik Road Station and Ramkund. Route SR-04 currently has a 22-minute delay; alternate routes are active.

**By train** — Nashik Road is the nearest railway station, 8 km from the main ghats. Pre-paid auto and shuttle counters are outside Exit 2.

**Parking** — Four designated pilgrim parking zones (Gangapur Camp, Panchvati East, Trimbakeshwar Road, Nashik Road Stn). Camp near Nashik Road Stn is least congested right now.

🚫 Private vehicles are not permitted within 1.5 km of Ramkund Ghat during Shahi Snan days — use the shuttle from the outer parking ring.`,
    mr: `**नाशिक व त्र्यंबकेश्वरमध्ये फिरणे**

**बसने** — नाशिक रोड स्थानक ते रामकुंड दरम्यान एमएसआरटीसी शटल दर 10–15 मिनिटांनी धावतात. मार्ग SR-04 सध्या 22 मिनिटे विलंबित आहे; पर्यायी मार्ग सक्रिय आहेत.

**रेल्वेने** — नाशिक रोड हे मुख्य घाटांपासून 8 किमी अंतरावरील सर्वात जवळचे स्थानक आहे. प्रीपेड ऑटो व शटल काउंटर एक्झिट 2 बाहेर आहेत.

**पार्किंग** — चार निश्चित यात्रेकरू पार्किंग झोन (गंगापूर कॅम्प, पंचवटी पूर्व, त्र्यंबकेश्वर रोड, नाशिक रोड स्थानक). सध्या नाशिक रोड स्थानकाजवळील कॅम्प सर्वात कमी गर्दीचा आहे.

🚫 शाही स्नान दिवशी रामकुंड घाटापासून 1.5 किमी परिसरात खासगी वाहनांना प्रवेश नाही — बाह्य पार्किंग रिंगमधून शटल वापरा.`,
    sources: ["MSRTC Live Tracker", "Mobility Intelligence"],
    followups: [
      { id: "transport-shuttle-ontime",         en: "Is the Nashik Road shuttle running on time?", mr: "नाशिक रोड शटल वेळेवर आहे का?" },
      { id: "transport-nearest-parking",        en: "Where's the nearest parking zone?", mr: "जवळचा पार्किंग झोन कुठे आहे?" },
      { id: "transport-private-vehicle-parking",en: "Is parking available for private vehicles today?", mr: "आज खासगी वाहनांसाठी पार्किंग उपलब्ध आहे का?" },
    ],
  },
  lostfound: {
    en: `**Lost & Found / Missing Persons**

If you're separated from your group or family, go to the nearest **Help Desk** (marked with a blue flag) or dial **1077** — every desk is linked to the central Missing Persons registry.

**Currently active cases:** 8 open, 6 resolved in the last 24 hours — most are reunited within 3 hours using CCTV face-match and help-desk announcements.

**What to do:**
1. Stay at the nearest help desk or ghat entry point — don't keep moving, it makes you harder to find.
2. Give a recent photo if you have one on your phone.
3. Announcements are made over the PA system in Marathi, Hindi and English every 15 minutes.

✅ Children and elderly pilgrims are given a wristband with a helpline number at every entry gate — ask at any checkpoint if you don't have one yet.`,
    mr: `**हरवलेल्या व्यक्ती / हरवले व सापडले**

आपण आपल्या गटापासून किंवा कुटुंबापासून वेगळे झाल्यास, जवळच्या **मदत केंद्रा**कडे जा (निळ्या झेंड्याने चिन्हांकित) किंवा **1077** डायल करा — प्रत्येक केंद्र केंद्रीय हरवलेल्या व्यक्ती नोंदीशी जोडलेले आहे.

**सध्या सक्रिय प्रकरणे:** गेल्या 24 तासांत 8 प्रलंबित, 6 निकाली — बहुतांश सीसीटीव्ही चेहरा-जुळणी व मदत केंद्र घोषणांद्वारे 3 तासांत सापडतात.

**काय करावे:**
1. जवळच्या मदत केंद्रावर किंवा घाट प्रवेशद्वारावर थांबा — सतत फिरू नका, त्यामुळे शोधणे कठीण होते.
2. आपल्या फोनमध्ये अलीकडील फोटो असल्यास तो द्या.
3. दर 15 मिनिटांनी मराठी, हिंदी व इंग्रजीत पीए प्रणालीवर घोषणा केल्या जातात.

✅ प्रत्येक प्रवेशद्वारावर लहान मुले व वृद्ध यात्रेकरूंना हेल्पलाइन क्रमांक असलेला मनगटी बँड दिला जातो — नसल्यास कोणत्याही तपासणी नाक्यावर विचारा.`,
    sources: ["Missing Persons Registry", "Field Copilot"],
    followups: [
      { id: "lostfound-helpline-number", en: "What's the missing persons helpline number?", mr: "हरवलेल्या व्यक्तींची हेल्पलाइन क्रमांक काय आहे?" },
      { id: "lostfound-reunite-time",    en: "How long does it usually take to reunite a missing person?", mr: "हरवलेली व्यक्ती सापडण्यास साधारण किती वेळ लागतो?" },
      { id: "lostfound-nearest-helpdesk",en: "Where's the nearest help desk to report someone missing?", mr: "हरवलेल्या व्यक्तीची तक्रार करण्यासाठी जवळचे मदत केंद्र कुठे आहे?" },
    ],
  },
  medical: {
    en: `**Medical & Health Camps**

**6 medical camps** are active right now, with **1,680 health staff** and **34 ambulances** on standby.

**Nearest camps:**
- Ramkund Ghat — full trauma + general OPD, open 24/7
- Panchvati — general OPD + first aid, 06:00–22:00
- Trimbakeshwar — general OPD + first aid, 06:00–22:00
- Nashik Road Stn — first aid post only

**Common concerns today:** heat exhaustion (advisory in effect, 38°C), minor dehydration, and blister/foot-care cases — free ORS and first aid at every camp, no charge.

⚠️ If you or someone nearby shows signs of heatstroke (confusion, hot dry skin, no sweating), get to the nearest camp immediately or flag any officer — don't wait in the queue.`,
    mr: `**वैद्यकीय व आरोग्य शिबिरे**

सध्या **6 वैद्यकीय शिबिरे** सक्रिय असून **1,680 आरोग्य कर्मचारी** व **34 रुग्णवाहिका** सज्ज आहेत.

**जवळची शिबिरे:**
- रामकुंड घाट — पूर्ण ट्रॉमा + सामान्य ओपीडी, 24/7 खुले
- पंचवटी — सामान्य ओपीडी + प्रथमोपचार, 06:00–22:00
- त्र्यंबकेश्वर — सामान्य ओपीडी + प्रथमोपचार, 06:00–22:00
- नाशिक रोड स्थानक — फक्त प्रथमोपचार केंद्र

**आजच्या सामान्य समस्या:** उष्माघाताचा त्रास (38°C साठी सूचना लागू), किरकोळ निर्जलीकरण व पायाला फोड — प्रत्येक शिबिरात मोफत ओआरएस व प्रथमोपचार उपलब्ध.

⚠️ उष्माघाताची लक्षणे (गोंधळ, कोरडी उष्ण त्वचा, घाम न येणे) दिसल्यास त्वरित जवळच्या शिबिरात जा किंवा कोणत्याही अधिकाऱ्याला सांगा — रांगेत थांबू नका.`,
    sources: ["Health & Epidemic Intelligence", "Medical Camp Registry"],
    followups: [
      { id: "medical-camp-trimbakeshwar",en: "Is there a medical camp open right now near Trimbakeshwar?", mr: "त्र्यंबकेश्वरजवळ वैद्यकीय शिबिर आत्ता सुरू आहे का?" },
      { id: "medical-heat-advisory",     en: "What are today's heat advisory details?", mr: "आजच्या उष्णतेच्या सूचनेचा तपशील काय आहे?" },
      { id: "medical-request-ambulance", en: "How do I request an ambulance?", mr: "रुग्णवाहिका कशी मागवावी?" },
    ],
  },
  schedule: {
    en: `**Shahi Snan & Akhara Schedule**

**Upcoming bathing dates:**
| Event | Date | T-minus | Readiness |
| Shravan Purnima | 11 Jul 2027 | 32 days | 78% |
| Simhastha Peak Snan | 12 Aug 2027 | 64 days | 62% |
| Anant Chaturdashi | 14 Sep 2027 | 97 days | 54% |

Akhara processions move in a fixed order from Panchvati to Ramkund starting at dawn on each Snan date — the exact procession order and timing is announced 48 hours in advance via the PA system and this console.

📈 Simhastha Peak Snan (12 Aug) is expected to draw the largest crowd of the season — an estimated 12 lakh pilgrims. Plan travel a day early if you can.`,
    mr: `**शाही स्नान व आखाडा वेळापत्रक**

**पुढील स्नान दिनांक:**
| कार्यक्रम | दिनांक | उर्वरित | सज्जता |
| श्रावण पौर्णिमा | 11 Jul 2027 | 32 दिवस | 78% |
| सिंहस्थ पर्वस्नान | 12 Aug 2027 | 64 दिवस | 62% |
| अनंत चतुर्दशी | 14 Sep 2027 | 97 दिवस | 54% |

आखाडा मिरवणुका प्रत्येक स्नान दिनांकी पहाटेपासून पंचवटी ते रामकुंड या निश्चित क्रमाने जातात — नेमका क्रम व वेळ 48 तास आधी पीए प्रणाली व या कन्सोलवर जाहीर केला जातो.

📈 सिंहस्थ पर्वस्नान (12 ऑगस्ट) या हंगामातील सर्वात मोठी गर्दी आणेल असा अंदाज आहे — अंदाजे 12 लाख यात्रेकरू. शक्य असल्यास एक दिवस आधी प्रवासाचे नियोजन करा.`,
    sources: ["Shahi Snan Planning Board", "War Room"],
    followups: [
      { id: "schedule-akhara-start-time",    en: "What time does the Akhara procession start?", mr: "आखाडा मिरवणूक किती वाजता सुरू होते?" },
      { id: "schedule-peak-snan-readiness",  en: "How ready are we for Simhastha Peak Snan?", mr: "सिंहस्थ पर्वस्नानासाठी आपण किती सज्ज आहोत?" },
      { id: "schedule-akhara-viewing-spot",  en: "Where's the best place to watch the Akhara procession?", mr: "आखाडा मिरवणूक पाहण्यासाठी सर्वोत्तम जागा कोणती?" },
    ],
  },
  weather: {
    en: `**Weather & River Conditions**

Nashik is currently **38°C**, clear skies — a heat advisory is in effect for all zones through the evening. Carry water and use the shaded rest points along the main ghat corridor.

**Godavari river level:** stable, well within the safe bathing threshold for all six ghats. No flood advisory is active.

☀️ Best time for the holy dip today: before 09:00 or after 17:00, when both crowd density and temperature are lower.`,
    mr: `**हवामान व नदीची स्थिती**

नाशिकमध्ये सध्या **38°C**, निरभ्र आकाश — संध्याकाळपर्यंत सर्व झोनसाठी उष्णतेचा इशारा लागू आहे. पाणी सोबत ठेवा व मुख्य घाट मार्गावरील सावलीच्या विश्रांती स्थळांचा वापर करा.

**गोदावरी नदीची पातळी:** स्थिर, सहाही घाटांसाठी सुरक्षित स्नान मर्यादेत. सध्या कोणताही पूर इशारा नाही.

☀️ आज पवित्र स्नानासाठी सर्वोत्तम वेळ: सकाळी 09:00 पूर्वी किंवा संध्याकाळी 17:00 नंतर, जेव्हा गर्दी व तापमान दोन्ही कमी असते.`,
    sources: ["IMD Nashik", "River Level Sensors"],
    followups: [
      { id: "weather-flood-risk",     en: "Is there a flood risk today?", mr: "आज पूराचा धोका आहे का?" },
      { id: "weather-best-visit-time",en: "According to today's weather, what's the best time to visit?", mr: "आजच्या हवामानानुसार सर्वोत्तम वेळ कोणती?" },
      { id: "weather-rain-evening",   en: "Will it rain this evening?", mr: "आज संध्याकाळी पाऊस पडेल का?" },
    ],
  },
  safety: {
    en: `**Safety & Emergency Contacts**

| Need | Number |
| Police / General Emergency | 100 |
| Ambulance | 108 |
| Missing Persons Helpline | 1077 |
| Disaster Management (NDRF) | 1070 |
| Women's Helpline | 1091 |

Every officer on the ground is reachable via the wristband QR code issued at entry gates. AI alerts are always **human-reviewed before any action is taken** — no automated enforcement.

🔴 In a genuine emergency, don't wait for an app — flag the nearest uniformed officer or dial 100 directly.`,
    mr: `**सुरक्षा व आपत्कालीन संपर्क**

| गरज | क्रमांक |
| पोलीस / सर्वसाधारण आपत्कालीन | 100 |
| रुग्णवाहिका | 108 |
| हरवलेल्या व्यक्ती हेल्पलाइन | 1077 |
| आपत्ती व्यवस्थापन (एनडीआरएफ) | 1070 |
| महिला हेल्पलाइन | 1091 |

प्रवेशद्वारावर दिलेल्या मनगटी बँडवरील क्यूआर कोडद्वारे जमिनीवरील प्रत्येक अधिकारी संपर्कात आहे. कोणतीही कारवाई होण्यापूर्वी एआय अलर्टचे **नेहमी मानवी पुनरावलोकन** केले जाते — कोणतीही स्वयंचलित कारवाई नाही.

🔴 खऱ्या आपत्कालीन परिस्थितीत अ‍ॅपची वाट पाहू नका — जवळच्या गणवेशधारी अधिकाऱ्याला सांगा किंवा थेट 100 डायल करा.`,
    sources: ["Emergency Response", "Command Authority Chain"],
    followups: [
      { id: "safety-contact-officer",        en: "How do I contact the nearest officer?", mr: "जवळच्या अधिकाऱ्याशी संपर्क कसा साधावा?" },
      { id: "safety-after-emergency-contact",en: "What happens after I contact emergency services?", mr: "आपत्कालीन सेवांशी संपर्क साधल्यानंतर काय होते?" },
      { id: "safety-womens-point",           en: "Is there a women's safety point nearby?", mr: "जवळपास महिला सुरक्षा केंद्र आहे का?" },
    ],
  },
  accommodation: {
    en: `**Accommodation & Stay**

Free pilgrim camps (tents, bedding, drinking water) are set up at **Gangapur Camp** and **Panchvati East**, run jointly by NMC and volunteer trusts — no booking needed, first-come basis.

Paid dharamshalas and guesthouses cluster around Ramkund and Trimbakeshwar; availability is tightest on Shahi Snan eve, so arriving a day early is strongly recommended.

🏕️ Gangapur Camp currently has the most space available and the shortest walk to the shuttle pickup point.`,
    mr: `**निवास व राहण्याची सोय**

**गंगापूर कॅम्प** व **पंचवटी पूर्व** येथे मोफत यात्रेकरू शिबिरे (तंबू, अंथरूण, पिण्याचे पाणी) उभारली आहेत, एनएमसी व स्वयंसेवी संस्थांद्वारे संयुक्तपणे — बुकिंगची गरज नाही, आधी येणाऱ्यास प्राधान्य.

रामकुंड व त्र्यंबकेश्वरजवळ सशुल्क धर्मशाळा व गेस्टहाऊस आहेत; शाही स्नानाच्या आदल्या रात्री जागा मिळणे सर्वात कठीण असते, त्यामुळे एक दिवस आधी येण्याची शिफारस आहे.

🏕️ गंगापूर कॅम्पमध्ये सध्या सर्वाधिक जागा उपलब्ध असून शटल थांब्यापर्यंतचे अंतरही सर्वात कमी आहे.`,
    sources: ["Pilgrim Guidance", "NMC Camp Registry"],
    followups: [
      { id: "accommodation-gangapur-free",     en: "Is Gangapur Camp free to stay at?", mr: "गंगापूर कॅम्पमध्ये राहणे मोफत आहे का?" },
      { id: "accommodation-gangapur-distance", en: "How far is Gangapur Camp from the main entry point?", mr: "गंगापूर कॅम्प मुख्य प्रवेशद्वारापासून किती दूर आहे?" },
      { id: "accommodation-dharamshala-advance",en: "Should I book a dharamshala in advance?", mr: "धर्मशाळा आधीच बुक करावी का?" },
    ],
  },
  facilities: {
    en: `**Toilets, Water & Facilities**

Free drinking-water points and toilet blocks are marked every 300m along the main ghat corridor — look for the blue-and-white signage.

**Current status:**
- Ramkund Ghat — passed hygiene inspection, fully operational
- Panchvati — passed, fully operational
- Trimbakeshwar — passed, fully operational
- Nashik Road Stn — under observation, cleaning crew on site

🚰 Chlorine and turbidity levels at every water point are tested every 2 hours and published on this console — all currently within safe limits.`,
    mr: `**शौचालय, पाणी व सुविधा**

मुख्य घाट मार्गावर दर 300 मीटरवर मोफत पिण्याचे पाणी व शौचालय ब्लॉक चिन्हांकित आहेत — निळ्या-पांढऱ्या पाटीसाठी लक्ष ठेवा.

**सध्याची स्थिती:**
- रामकुंड घाट — स्वच्छता तपासणी उत्तीर्ण, पूर्णपणे कार्यरत
- पंचवटी — उत्तीर्ण, पूर्णपणे कार्यरत
- त्र्यंबकेश्वर — उत्तीर्ण, पूर्णपणे कार्यरत
- नाशिक रोड स्थानक — निरीक्षणाधीन, स्वच्छता पथक जागेवर

🚰 प्रत्येक जल केंद्रावरील क्लोरीन व गढूळपणा दर 2 तासांनी तपासला जातो व या कन्सोलवर प्रकाशित केला जातो — सध्या सर्व सुरक्षित मर्यादेत.`,
    sources: ["Service Stress Monitoring", "Water Quality Sensors"],
    followups: [
      { id: "facilities-water-safe",     en: "Is the water safe to drink right now?", mr: "सध्या पाणी पिण्यायोग्य आहे का?" },
      { id: "facilities-nearest-toilet", en: "Where's the nearest toilet block?", mr: "जवळचे शौचालय ब्लॉक कुठे आहे?" },
      { id: "facilities-last-inspection",en: "When was the last hygiene inspection of the water points?", mr: "पाण्याच्या केंद्रांची शेवटची स्वच्छता तपासणी कधी झाली?" },
    ],
  },
  volunteer: {
    en: `**Volunteering & Donations**

Registered volunteers get an official badge, meals and a duty roster — sign up at any **Seva Registration Desk** (Ramkund, Panchvati, Gangapur Camp) or online via the Maha Kumbh AI portal.

**Current volunteer strength:** 4,200 active across 6 zones — helping with crowd guidance, lost & found, water distribution and first aid support.

**Ways to help:**
1. Register for a shift at a Seva Desk — 4-hour slots, ID required.
2. Donate essentials (drinking water, ORS, blankets) at any NMC collection point.
3. Trained medical/paramedic volunteers can register directly at a Medical Camp.

🤝 All volunteers undergo a basic safety briefing before their first shift — walk-ins are welcome, no prior registration needed to attend.`,
    mr: `**स्वयंसेवा व देणगी**

नोंदणीकृत स्वयंसेवकांना अधिकृत बॅज, जेवण व कर्तव्य वेळापत्रक मिळते — कोणत्याही **सेवा नोंदणी केंद्रा**वर (रामकुंड, पंचवटी, गंगापूर कॅम्प) किंवा कुंभ एआय पोर्टलवर ऑनलाइन नोंदणी करा.

**सध्याची स्वयंसेवक संख्या:** 6 झोनमध्ये 4,200 सक्रिय — गर्दी मार्गदर्शन, हरवले व सापडले, पाणी वाटप व प्रथमोपचारात मदत.

**मदत करण्याचे मार्ग:**
1. सेवा केंद्रावर पाळीसाठी नोंदणी करा — 4 तासांचे स्लॉट, ओळखपत्र आवश्यक.
2. कोणत्याही एनएमसी संकलन केंद्रावर आवश्यक वस्तू (पिण्याचे पाणी, ओआरएस, ब्लँकेट) दान करा.
3. प्रशिक्षित वैद्यकीय/पॅरामेडिक स्वयंसेवक थेट वैद्यकीय शिबिरात नोंदणी करू शकतात.

🤝 पहिल्या पाळीपूर्वी सर्व स्वयंसेवकांना मूलभूत सुरक्षा माहिती दिली जाते — प्रत्यक्ष उपस्थित राहणाऱ्यांचे स्वागत आहे, उपस्थितीसाठी आधीच्या नोंदणीची गरज नाही.`,
    sources: ["Seva Registration Portal", "Volunteer Coordination Cell"],
    followups: [
      { id: "volunteer-nearest-seva-desk", en: "Where's the nearest Seva Registration Desk?", mr: "जवळचे सेवा नोंदणी केंद्र कुठे आहे?" },
      { id: "volunteer-donate-directly",   en: "Can I donate essentials or supplies directly?", mr: "मी थेट आवश्यक वस्तू दान करू शकतो का?" },
      { id: "volunteer-id-required",       en: "Do I need ID to volunteer?", mr: "स्वयंसेवा करण्यासाठी ओळखपत्र आवश्यक आहे का?" },
    ],
  },
  atm: {
    en: `**ATMs & Cash Points**

**12 mobile ATM vans** and **8 fixed bank kiosks** are positioned across the ghat corridor and camps — look for the SBI, Bank of Maharashtra and HDFC branded vans.

**Nearest locations:**
- Ramkund Ghat — 2 fixed ATMs near Entry 2, plus 1 mobile van
- Nashik Road Stn — 3 ATMs inside the station complex
- Gangapur Camp — 1 mobile van, rotates hourly between camp zones
- Panchvati / Trimbakeshwar — mobile vans on a published 2-hour rotation

**Digital payments:** UPI and QR-code payments are accepted at almost all shops, camps and stalls — a good backup if a nearby ATM queue is long.

💳 Mobile ATM van locations update live on this console — ask again if you're in a different zone for the current nearest one.`,
    mr: `**एटीएम व रोकड केंद्रे**

घाट मार्ग व शिबिरांमध्ये **12 फिरती एटीएम व्हॅन** व **8 स्थिर बँक कक्ष** आहेत — एसबीआय, बँक ऑफ महाराष्ट्र व एचडीएफसी चिन्हांकित व्हॅन्ससाठी लक्ष ठेवा.

**जवळची ठिकाणे:**
- रामकुंड घाट — प्रवेशद्वार 2 जवळ 2 स्थिर एटीएम, तसेच 1 फिरती व्हॅन
- नाशिक रोड स्थानक — स्थानक परिसरात 3 एटीएम
- गंगापूर कॅम्प — 1 फिरती व्हॅन, दर तासाला शिबिर झोनमध्ये फिरते
- पंचवटी / त्र्यंबकेश्वर — प्रकाशित 2-तासांच्या फेरीनुसार फिरत्या व्हॅन

**डिजिटल पेमेंट:** जवळपास सर्व दुकाने, शिबिरे व स्टॉलवर यूपीआय व क्यूआर-कोड पेमेंट स्वीकारले जाते — जवळचे एटीएम रांगेत असल्यास चांगला पर्याय.

💳 फिरत्या एटीएम व्हॅनचे स्थान या कन्सोलवर थेट अद्ययावत होते — वेगळ्या झोनमध्ये असल्यास पुन्हा विचारा.`,
    sources: ["Bank Coordination Cell", "Digital Payment Helpdesk"],
    followups: [
      { id: "atm-upi-stalls",     en: "Is UPI accepted at pilgrim stalls?", mr: "यात्रेकरू स्टॉलवर यूपीआय स्वीकारले जाते का?" },
      { id: "atm-nearest-to-gate",en: "Where's the nearest ATM to the main entry gate?", mr: "मुख्य प्रवेशद्वाराजवळचे एटीएम कुठे आहे?" },
      { id: "atm-cashback-stall", en: "Can I get cash back at a stall?", mr: "स्टॉलवर कॅश बॅक मिळू शकते का?" },
    ],
  },
};

// ── Answers for individual follow-up chips — each drills into that exact
// question instead of re-showing the parent topic's full overview. Keyed by
// the same ids used in RESPONSES[topic].followups. ──
const FOLLOWUP_RESPONSES = {
  "crowd-diversion-plan": {
    en: `**Ramkund Entry 2 → Entry 4.** Since density crossed 9.2/m² this diversion has been active for 22 minutes — Beat Squad B12 is managing the flow with megaphone announcements, and Crowd Control Van CC-04 is on standby at Entry 4 in case a second diversion point is needed.

🔴 If Entry 4 also crosses 85% load, the next fallback is a temporary hold at the outer barricade until Ramkund eases.`,
    mr: `**रामकुंड प्रवेशद्वार 2 → प्रवेशद्वार 4.** घनता 9.2/m² ओलांडल्यापासून हे वळण गेल्या 22 मिनिटांपासून सक्रिय आहे — बीट स्क्वॉड B12 मेगाफोन घोषणांद्वारे प्रवाह व्यवस्थापित करत आहे, आणि गर्दी नियंत्रण व्हॅन CC-04 आवश्यक असल्यास दुसऱ्या वळण बिंदूसाठी प्रवेशद्वार 4 वर सज्ज आहे.

🔴 प्रवेशद्वार 4 देखील 85% पेक्षा जास्त भारित झाल्यास, पुढील पर्याय रामकुंडवरील गर्दी कमी होईपर्यंत बाह्य बॅरिकेडवर तात्पुरता थांबा असेल.`,
    sources: ["Crowd Risk Engine", "War Room"],
  },
  "crowd-shravan-trend": {
    en: `**Shravan Purnima (11 Jul 2027) is 32 days out**, and early modelling puts expected peak density at Ramkund around 8.5–9/m² — slightly below today's 9.2%, but for a longer sustained window (dawn to mid-morning) since it's a lighter bathing date than Simhastha Peak Snan.

📈 Readiness for that date currently stands at 78%, with NDRF pre-positioning and extra medical camps still pending.`,
    mr: `**श्रावण पौर्णिमा (11 जुलै 2027) 32 दिवसांवर आहे**, आणि प्राथमिक मॉडेलिंगनुसार रामकुंडवरील अपेक्षित पीक घनता सुमारे 8.5–9/m² असेल — आजच्या 9.2% पेक्षा किंचित कमी, पण सिंहस्थ पर्वस्नानापेक्षा हलक्या स्नान दिनांकामुळे जास्त कालावधीसाठी (पहाटेपासून सकाळपर्यंत) टिकणारी.

📈 त्या दिनांकासाठी सज्जता सध्या 78% असून, एनडीआरएफ पूर्व-तैनाती व अतिरिक्त वैद्यकीय शिबिरे अजून प्रलंबित आहेत.`,
    sources: ["Digital Twin Sim", "Shahi Snan Planning Board"],
  },
  "crowd-barricades-today": {
    en: `**Panchvati** is the priority — it's trending up ahead of the 15:30 CM visit, so extra barricades are being pre-positioned there now. **Ramkund** already has full barricading active from the Entry 2 diversion.

**Trimbakeshwar** and the remaining three zones are steady and don't need additional barricades at this time.`,
    mr: `**पंचवटी** प्राधान्याची आहे — मुख्यमंत्र्यांच्या 15:30 भेटीपूर्वी तेथील गर्दी वाढत असल्याने आता अतिरिक्त बॅरिकेड्स तैनात केले जात आहेत. **रामकुंड**वर प्रवेशद्वार 2 वळणामुळे आधीच पूर्ण बॅरिकेडिंग सक्रिय आहे.

**त्र्यंबकेश्वर** व उर्वरित तीन झोन स्थिर असून सध्या अतिरिक्त बॅरिकेड्सची गरज नाही.`,
    sources: ["Crowd Risk Engine"],
  },
  "transport-shuttle-ontime": {
    en: `**Route SR-04 is running 22 minutes behind** schedule due to the Ramkund crowd diversion — all other MSRTC shuttle routes (SR-01, SR-02, SR-03) are on time at their normal 10–15 minute frequency.

🚫 If you're on SR-04, an alternate route via Panchvati East is active and adds roughly 8 minutes.`,
    mr: `**मार्ग SR-04 वेळापत्रकापेक्षा 22 मिनिटे विलंबित** आहे, रामकुंड गर्दी वळणामुळे — इतर सर्व एमएसआरटीसी शटल मार्ग (SR-01, SR-02, SR-03) नेहमीच्या 10–15 मिनिटांच्या फेरीनुसार वेळेवर आहेत.

🚫 आपण SR-04 वर असल्यास, पंचवटी पूर्वमार्गे पर्यायी मार्ग सक्रिय असून त्यामुळे अंदाजे 8 मिनिटे अधिक लागतात.`,
    sources: ["MSRTC Live Tracker"],
  },
  "transport-nearest-parking": {
    en: `**Nashik Road Stn parking zone** is currently least congested and the recommended default. The other three — Gangapur Camp, Panchvati East and Trimbakeshwar Road — are filling up faster as the day progresses.

🅿️ All four zones are free of charge for registered pilgrim vehicles.`,
    mr: `**नाशिक रोड स्थानक पार्किंग झोन** सध्या सर्वात कमी गर्दीचा असून शिफारसीय डिफॉल्ट आहे. इतर तीन — गंगापूर कॅम्प, पंचवटी पूर्व व त्र्यंबकेश्वर रोड — दिवस जसजसा पुढे जातो तसतसे वेगाने भरत आहेत.

🅿️ नोंदणीकृत यात्रेकरू वाहनांसाठी चारही झोन मोफत आहेत.`,
    sources: ["Mobility Intelligence"],
  },
  "transport-private-vehicle-parking": {
    en: `🚫 **No, not within 1.5 km of Ramkund Ghat** — private vehicles are restricted there during Shahi Snan days. The four designated parking zones (outside that radius) remain open to private vehicles as normal; use the shuttle for the last stretch.`,
    mr: `🚫 **नाही, रामकुंड घाटापासून 1.5 किमी परिसरात नाही** — शाही स्नान दिवशी तेथे खासगी वाहनांवर बंदी आहे. चार निश्चित पार्किंग झोन (त्या परिघाबाहेर) खासगी वाहनांसाठी नेहमीप्रमाणे खुले आहेत; शेवटच्या टप्प्यासाठी शटल वापरा.`,
    sources: ["Mobility Intelligence", "MSRTC Live Tracker"],
  },
  "lostfound-helpline-number": {
    en: `📞 **Dial 1077** — the dedicated Missing Persons helpline, staffed 24/7 and linked directly to the central registry. You can also flag any officer wearing a wristband QR code; they'll raise the case from the field.

For a general emergency alongside a missing-person case, 100 also connects you to Police.`,
    mr: `📞 **1077 डायल करा** — समर्पित हरवलेल्या व्यक्ती हेल्पलाइन, 24/7 कार्यरत व थेट केंद्रीय नोंदीशी जोडलेली. मनगटी बँडवरील क्यूआर कोड असलेल्या कोणत्याही अधिकाऱ्यालाही सांगू शकता; ते क्षेत्रातूनच प्रकरण नोंदवतील.

हरवलेल्या व्यक्तीसोबत सर्वसाधारण आपत्कालीन परिस्थिती असल्यास, 100 देखील पोलिसांशी जोडते.`,
    sources: ["Missing Persons Registry"],
  },
  "lostfound-reunite-time": {
    en: `**Most cases resolve within 3 hours** using CCTV face-match combined with help-desk PA announcements. Of the 8 currently open cases, 6 were resolved in the last 24 hours within that window.

✅ Cases involving a child or elderly pilgrim with a wristband are typically fastest, since the wristband ID speeds up the match.`,
    mr: `**बहुतांश प्रकरणे 3 तासांत निकाली** निघतात, सीसीटीव्ही चेहरा-जुळणी व मदत केंद्र पीए घोषणांच्या मदतीने. सध्या सक्रिय असलेल्या 8 प्रकरणांपैकी, गेल्या 24 तासांत 6 याच कालावधीत निकाली निघाली.

✅ मनगटी बँड असलेल्या लहान मूल किंवा वृद्ध यात्रेकरूंची प्रकरणे साधारणपणे सर्वात जलद निकाली निघतात, कारण बँड आयडीमुळे जुळणी वेगवान होते.`,
    sources: ["Missing Persons Registry", "Field Copilot"],
  },
  "lostfound-nearest-helpdesk": {
    en: `Every **Help Desk** (marked with a blue flag) can register a missing-person case — the nearest ones are at each ghat's main entry point and every camp's reception. Stay put once you've reported it; the registry is shared across all desks so you don't need to keep moving between them.

📞 If you can't reach one quickly, dial 1077 instead.`,
    mr: `प्रत्येक **मदत केंद्र** (निळ्या झेंड्याने चिन्हांकित) हरवलेल्या व्यक्तीचे प्रकरण नोंदवू शकते — जवळचे केंद्र प्रत्येक घाटाच्या मुख्य प्रवेशद्वारावर व प्रत्येक शिबिराच्या स्वागत कक्षात आहे. नोंदणी केल्यावर तिथेच थांबा; नोंद सर्व केंद्रांमध्ये सामायिक असल्याने फिरण्याची गरज नाही.

📞 लवकर पोहोचता येत नसल्यास, 1077 डायल करा.`,
    sources: ["Missing Persons Registry"],
  },
  "medical-camp-trimbakeshwar": {
    en: `✅ **Yes — Trimbakeshwar's camp is open now**, general OPD + first aid, 06:00–22:00. It's not the 24/7 trauma-level camp (that's Ramkund), so for anything beyond first aid, Ramkund or an ambulance transfer is the next step.

🚑 34 ambulances are on standby fleet-wide if transfer is needed.`,
    mr: `✅ **होय — त्र्यंबकेश्वरचे शिबिर आत्ता सुरू आहे**, सामान्य ओपीडी + प्रथमोपचार, 06:00–22:00. हे 24/7 ट्रॉमा-स्तरीय शिबिर नाही (ते रामकुंड आहे), त्यामुळे प्रथमोपचारापलीकडे काही आवश्यक असल्यास, रामकुंड किंवा रुग्णवाहिका हस्तांतरण हा पुढील टप्पा आहे.

🚑 हस्तांतरण आवश्यक असल्यास संपूर्ण ताफ्यात 34 रुग्णवाहिका सज्ज आहेत.`,
    sources: ["Medical Camp Registry"],
  },
  "medical-heat-advisory": {
    en: `⚠️ **38°C, advisory in effect through the evening.** Watch for heat exhaustion (fatigue, heavy sweating, dizziness) and heatstroke (confusion, hot dry skin, no sweating) — the second is a medical emergency, get to a camp immediately.

Free ORS and shaded rest points are available along the main ghat corridor — use them proactively, don't wait for symptoms.`,
    mr: `⚠️ **38°C, संध्याकाळपर्यंत सूचना लागू.** उष्माघाताच्या सुरुवातीच्या लक्षणांकडे लक्ष द्या (थकवा, जास्त घाम, चक्कर) व उष्माघात (गोंधळ, कोरडी उष्ण त्वचा, घाम न येणे) — दुसरे वैद्यकीय आणीबाणी आहे, त्वरित शिबिरात जा.

मुख्य घाट मार्गावर मोफत ओआरएस व सावलीचे विश्रांती स्थळ उपलब्ध आहेत — लक्षणांची वाट न पाहता आधीच वापरा.`,
    sources: ["Health & Epidemic Intelligence", "IMD Nashik"],
  },
  "medical-request-ambulance": {
    en: `🚑 Flag any officer or head to the nearest medical camp — every camp can dispatch from the 34-ambulance fleet directly. For a faster route, dial **108** (the dedicated ambulance line) and give your nearest landmark or zone.

If the patient is blocked by crowd (like a route-access case), mention that explicitly — Police escort can be arranged alongside the ambulance.`,
    mr: `🚑 कोणत्याही अधिकाऱ्याला सांगा किंवा जवळच्या वैद्यकीय शिबिरात जा — प्रत्येक शिबिर 34-रुग्णवाहिका ताफ्यातून थेट पाठवू शकते. जलद मार्गासाठी, **108** (समर्पित रुग्णवाहिका क्रमांक) डायल करा व जवळची खूण किंवा झोन सांगा.

रुग्ण गर्दीमुळे अडकला असल्यास (मार्ग-प्रवेश प्रकरण), ते स्पष्टपणे सांगा — रुग्णवाहिकेसोबत पोलीस एस्कॉर्टही व्यवस्था करता येते.`,
    sources: ["Health & Epidemic Intelligence"],
  },
  "schedule-akhara-start-time": {
    en: `Akhara processions begin **at dawn** on each Snan date, moving from Panchvati to Ramkund in a fixed order. The exact start time and procession sequence for the next date is confirmed and published 48 hours in advance — check back closer to Shravan Purnima (11 Jul) for the locked schedule.

📢 It's also announced over the PA system on the day.`,
    mr: `आखाडा मिरवणुका प्रत्येक स्नान दिनांकी **पहाटे** सुरू होतात, पंचवटी ते रामकुंड या निश्चित क्रमाने. पुढील दिनांकाची नेमकी सुरुवात वेळ व मिरवणूक क्रम 48 तास आधी निश्चित करून प्रकाशित केला जातो — श्रावण पौर्णिमा (11 जुलै) जवळ आल्यावर निश्चित वेळापत्रक तपासा.

📢 त्या दिवशी पीए प्रणालीवरही घोषणा केली जाते.`,
    sources: ["Shahi Snan Planning Board"],
  },
  "schedule-peak-snan-readiness": {
    en: `**62% ready** — the lowest of the three upcoming dates, since Simhastha Peak Snan (12 Aug) is expected to be the season's largest, at an estimated 12 lakh pilgrims.

📋 Still pending: mass evacuation route finalization, an all-agency simulation drill, satellite comms backup, 20 additional ambulances, and the media embargo protocol. Crowd risk modelling and the digital twin scenario are already done.`,
    mr: `**62% सज्ज** — पुढील तीन दिनांकांपैकी सर्वात कमी, कारण सिंहस्थ पर्वस्नान (12 ऑगस्ट) या हंगामातील सर्वात मोठे असेल असा अंदाज आहे, अंदाजे 12 लाख यात्रेकरू.

📋 अजून प्रलंबित: मोठ्या प्रमाणावर स्थलांतर मार्ग निश्चिती, सर्व-संस्था सराव कवायत, उपग्रह संदेशवहन बॅकअप, 20 अतिरिक्त रुग्णवाहिका, व माध्यम प्रतिबंध प्रोटोकॉल. गर्दी जोखीम मॉडेलिंग व डिजिटल ट्विन परिदृश्य आधीच पूर्ण झाले आहे.`,
    sources: ["Shahi Snan Planning Board", "War Room"],
  },
  "schedule-akhara-viewing-spot": {
    en: `The stretch along **Panchvati Road near the starting point** offers the clearest view as the procession forms up, before the crowd builds. Ramkund itself gets extremely dense as the procession arrives — arrive at least 90 minutes early if you want to watch from there instead.

🟡 Panchvati is already trending up in density ahead of today's events — plan for a wait.`,
    mr: `**पंचवटी रोडवरील सुरुवातीच्या ठिकाणाजवळील भाग** मिरवणूक तयार होताना, गर्दी वाढण्यापूर्वी सर्वात स्पष्ट दृश्य देतो. मिरवणूक पोहोचताच रामकुंड स्वतः अत्यंत गर्दीचे होते — तिथून पाहायचे असल्यास किमान 90 मिनिटे आधी पोहोचा.

🟡 पंचवटीतील घनता आजच्या कार्यक्रमांपूर्वी आधीच वाढत आहे — प्रतीक्षेसाठी नियोजन करा.`,
    sources: ["Shahi Snan Planning Board", "Crowd Risk Engine"],
  },
  "weather-flood-risk": {
    en: `✅ **No — the Godavari river level is stable** and well within the safe bathing threshold for all six ghats. No flood advisory is active anywhere in the zone right now.

The only active advisory today is for heat (38°C), not water levels.`,
    mr: `✅ **नाही — गोदावरी नदीची पातळी स्थिर आहे** व सहाही घाटांसाठी सुरक्षित स्नान मर्यादेत आहे. सध्या झोनमध्ये कोठेही पूर इशारा सक्रिय नाही.

आजची एकमेव सक्रिय सूचना उष्णतेसाठी (38°C) आहे, पाण्याच्या पातळीसाठी नाही.`,
    sources: ["River Level Sensors"],
  },
  "weather-best-visit-time": {
    en: `☀️ **Before 09:00 or after 17:00** — both crowd density and temperature are noticeably lower in those windows. Midday (12:00–15:00) combines the day's peak heat with peak ghat crowding, so it's the window to avoid if your visit is flexible.`,
    mr: `☀️ **सकाळी 09:00 पूर्वी किंवा संध्याकाळी 17:00 नंतर** — या वेळेत गर्दी व तापमान दोन्ही लक्षणीयरीत्या कमी असते. दुपार (12:00–15:00) दिवसातील सर्वाधिक उष्णता व सर्वाधिक घाट गर्दी एकत्र आणते, त्यामुळे शक्य असल्यास ही वेळ टाळा.`,
    sources: ["IMD Nashik", "Crowd Risk Engine"],
  },
  "weather-rain-evening": {
    en: `According to the current forecast, **no rain is expected this evening** — skies remain clear with the heat advisory continuing through the evening hours. This can change quickly during monsoon season, so check back if your plans depend on it.`,
    mr: `सध्याच्या अंदाजानुसार, **आज संध्याकाळी पाऊस अपेक्षित नाही** — आकाश निरभ्र राहील व उष्णतेची सूचना संध्याकाळपर्यंत कायम राहील. पावसाळी हंगामात हे वेगाने बदलू शकते, त्यामुळे आपले नियोजन यावर अवलंबून असल्यास पुन्हा तपासा.`,
    sources: ["IMD Nashik"],
  },
  "safety-contact-officer": {
    en: `Every officer on the ground carries a **wristband QR code** issued at entry gates — scan it (or ask any nearby officer to help scan) to reach them directly. If you don't have a wristband, flag any uniformed officer visually; they're posted at every ghat entry and camp.

🔴 For anything urgent, don't search for a specific officer — dial 100 instead.`,
    mr: `जमिनीवरील प्रत्येक अधिकारी प्रवेशद्वारावर दिलेला **मनगटी बँड क्यूआर कोड** बाळगतो — तो स्कॅन करा (किंवा जवळच्या कोणत्याही अधिकाऱ्याला स्कॅन करण्यास मदत करण्यास सांगा) थेट संपर्कासाठी. मनगटी बँड नसल्यास, कोणत्याही गणवेशधारी अधिकाऱ्याला थेट सांगा; ते प्रत्येक घाट प्रवेशद्वार व शिबिरावर तैनात आहेत.

🔴 तातडीच्या कोणत्याही गोष्टीसाठी, विशिष्ट अधिकारी शोधू नका — त्याऐवजी 100 डायल करा.`,
    sources: ["Command Authority Chain"],
  },
  "safety-after-emergency-contact": {
    en: `Your call is logged and routed to the nearest available unit — Police (100), Ambulance (108) or NDRF (1070) depending on the number dialed. A field officer or unit is dispatched, and **every AI-assisted alert is human-reviewed before any action is taken** — nothing happens automatically.

You'll typically see a responding officer or unit within minutes for anything flagged urgent.`,
    mr: `आपला कॉल नोंदवला जातो व डायल केलेल्या क्रमांकानुसार जवळच्या उपलब्ध तुकडीकडे — पोलीस (100), रुग्णवाहिका (108) किंवा एनडीआरएफ (1070) — वळवला जातो. क्षेत्रीय अधिकारी किंवा तुकडी पाठवली जाते, आणि **प्रत्येक एआय-सहाय्यित अलर्टचे कोणतीही कारवाई होण्यापूर्वी मानवी पुनरावलोकन होते** — काहीही स्वयंचलितपणे घडत नाही.

तातडीचे चिन्हांकित केलेल्या कोणत्याही गोष्टीसाठी सामान्यतः काही मिनिटांत प्रतिसाद देणारा अधिकारी किंवा तुकडी दिसेल.`,
    sources: ["Command Authority Chain", "Emergency Response"],
  },
  "safety-womens-point": {
    en: `✅ **Yes — a women's safety point is at every major ghat and camp**, staffed with women officers and volunteers, alongside the dedicated Women's Helpline (**1091**). These points also handle any women's health or hygiene needs.

Look for the pink-flagged desk near each ghat's main entry.`,
    mr: `✅ **होय — प्रत्येक प्रमुख घाट व शिबिरावर महिला सुरक्षा केंद्र आहे**, महिला अधिकारी व स्वयंसेवकांसह, समर्पित महिला हेल्पलाइन (**1091**) सोबत. ही केंद्रे महिलांच्या आरोग्य किंवा स्वच्छतेच्या गरजाही हाताळतात.

प्रत्येक घाटाच्या मुख्य प्रवेशद्वाराजवळ गुलाबी झेंडा असलेले केंद्र पहा.`,
    sources: ["Emergency Response"],
  },
  "accommodation-gangapur-free": {
    en: `✅ **Yes, completely free** — tents, bedding and drinking water, no booking or payment needed. It's run jointly by NMC and volunteer trusts on a first-come basis.

It currently has the most open space of the two free camps, so availability isn't a concern right now.`,
    mr: `✅ **होय, पूर्णपणे मोफत** — तंबू, अंथरूण व पिण्याचे पाणी, बुकिंग किंवा पैसे देण्याची गरज नाही. एनएमसी व स्वयंसेवी संस्थांद्वारे संयुक्तपणे आधी येणाऱ्यास प्राधान्य या तत्त्वावर चालवले जाते.

दोन मोफत शिबिरांपैकी सध्या येथे सर्वाधिक जागा उपलब्ध आहे, त्यामुळे सध्या उपलब्धतेची चिंता नाही.`,
    sources: ["NMC Camp Registry"],
  },
  "accommodation-gangapur-distance": {
    en: `Gangapur Camp has the **shortest walk to the shuttle pickup point** of all four parking/camp zones — a few minutes on foot. It's also one of the four designated parking zones, so if you're driving in, this is a convenient combined stop.

🚌 From there, MSRTC shuttles run every 10–15 minutes toward the main ghats.`,
    mr: `गंगापूर कॅम्पवरून शटल थांब्यापर्यंतचे अंतर चारही पार्किंग/शिबिर झोनमध्ये **सर्वात कमी** आहे — काही मिनिटांचे अंतर. हे चार निश्चित पार्किंग झोनपैकी एकही आहे, त्यामुळे वाहनाने येत असल्यास हा सोयीस्कर संयुक्त थांबा आहे.

🚌 तेथून, एमएसआरटीसी शटल मुख्य घाटांकडे दर 10–15 मिनिटांनी धावतात.`,
    sources: ["NMC Camp Registry", "Mobility Intelligence"],
  },
  "accommodation-dharamshala-advance": {
    en: `⚠️ **Yes, strongly recommended** — paid dharamshalas and guesthouses around Ramkund and Trimbakeshwar fill up fastest on Shahi Snan eve. If you can't book ahead, arriving a full day early significantly improves your chances.

The two free camps (Gangapur Camp, Panchvati East) remain a reliable no-booking fallback either way.`,
    mr: `⚠️ **होय, जोरदार शिफारस केली जाते** — रामकुंड व त्र्यंबकेश्वरजवळील सशुल्क धर्मशाळा व गेस्टहाऊस शाही स्नानाच्या आदल्या रात्री सर्वात वेगाने भरतात. आधी बुकिंग करता येत नसल्यास, एक पूर्ण दिवस आधी पोहोचल्याने शक्यता लक्षणीयरीत्या वाढते.

दोन्ही मोफत शिबिरे (गंगापूर कॅम्प, पंचवटी पूर्व) कोणत्याही परिस्थितीत विश्वासार्ह बुकिंग-मुक्त पर्याय आहेत.`,
    sources: ["Pilgrim Guidance"],
  },
  "facilities-water-safe": {
    en: `✅ **Yes — currently within safe limits** at all tested points. Chlorine and turbidity levels are checked every 2 hours and published live on this console, so the answer can change through the day — ask again if you want the latest reading for your zone.

🚰 Free drinking-water points are marked every 300m along the main ghat corridor.`,
    mr: `✅ **होय — सध्या सुरक्षित मर्यादेत** सर्व तपासलेल्या केंद्रांवर. क्लोरीन व गढूळपणा दर 2 तासांनी तपासला जातो व या कन्सोलवर थेट प्रकाशित केला जातो, त्यामुळे उत्तर दिवसभरात बदलू शकते — आपल्या झोनसाठी नवीनतम वाचन हवे असल्यास पुन्हा विचारा.

🚰 मुख्य घाट मार्गावर दर 300 मीटरवर मोफत पिण्याचे पाणी केंद्र चिन्हांकित आहेत.`,
    sources: ["Water Quality Sensors"],
  },
  "facilities-nearest-toilet": {
    en: `Toilet blocks are marked every **300m along the main ghat corridor** with blue-and-white signage — the nearest one is never far regardless of which ghat you're at. Ramkund, Panchvati and Trimbakeshwar blocks are all fully operational right now.

Nashik Road Stn's block is under observation with a cleaning crew on site, so expect a short wait there.`,
    mr: `शौचालय ब्लॉक मुख्य घाट मार्गावर दर **300 मीटरवर** निळ्या-पांढऱ्या पाटीसह चिन्हांकित आहेत — आपण कोणत्याही घाटावर असलात तरी जवळचे कधीच दूर नसते. रामकुंड, पंचवटी व त्र्यंबकेश्वर ब्लॉक सध्या पूर्णपणे कार्यरत आहेत.

नाशिक रोड स्थानकाचा ब्लॉक निरीक्षणाधीन असून स्वच्छता पथक जागेवर आहे, त्यामुळे तेथे थोडी प्रतीक्षा अपेक्षित आहे.`,
    sources: ["Service Stress Monitoring"],
  },
  "facilities-last-inspection": {
    en: `**Every 2 hours** — chlorine and turbidity levels at every water point are tested on that cycle and published live on this console. Ramkund, Panchvati and Trimbakeshwar all passed their most recent inspection; Nashik Road Stn's block is under observation with a cleaning crew currently on site.

🚰 All readings are within safe limits as of the last cycle.`,
    mr: `**दर 2 तासांनी** — प्रत्येक जल केंद्रावरील क्लोरीन व गढूळपणा या फेरीत तपासले जातात व या कन्सोलवर थेट प्रकाशित केले जातात. रामकुंड, पंचवटी व त्र्यंबकेश्वर सर्वांनी अलीकडील तपासणी उत्तीर्ण केली; नाशिक रोड स्थानकाचा ब्लॉक निरीक्षणाधीन असून स्वच्छता पथक सध्या जागेवर आहे.

🚰 शेवटच्या फेरीनुसार सर्व वाचने सुरक्षित मर्यादेत आहेत.`,
    sources: ["Water Quality Sensors"],
  },
  "volunteer-nearest-seva-desk": {
    en: `Seva Registration Desks are at **Ramkund, Panchvati and Gangapur Camp** — any of the three works, and you can also register online via the Maha Kumbh AI portal if you'd rather skip the queue. Bring an ID for the in-person option.

🤝 4,200 volunteers are already active across all 6 zones, so desks are well-staffed right now.`,
    mr: `सेवा नोंदणी केंद्रे **रामकुंड, पंचवटी व गंगापूर कॅम्प** येथे आहेत — तिन्हीपैकी कोणतेही चालेल, आणि रांग टाळायची असल्यास कुंभ एआय पोर्टलवर ऑनलाइनही नोंदणी करू शकता. प्रत्यक्ष नोंदणीसाठी ओळखपत्र सोबत ठेवा.

🤝 सर्व 6 झोनमध्ये आधीच 4,200 स्वयंसेवक सक्रिय आहेत, त्यामुळे केंद्रे सध्या पुरेशी कर्मचारीयुक्त आहेत.`,
    sources: ["Seva Registration Portal"],
  },
  "volunteer-donate-directly": {
    en: `✅ **Yes — drop essentials at any NMC collection point.** Drinking water, ORS and blankets are the most needed items right now given the heat advisory. No registration is required just to donate.

If you'd rather donate through a formal channel, the Maha Kumbh AI portal also accepts contributions that get routed to collection points directly.`,
    mr: `✅ **होय — कोणत्याही एनएमसी संकलन केंद्रावर आवश्यक वस्तू द्या.** उष्णतेच्या सूचनेमुळे सध्या पिण्याचे पाणी, ओआरएस व ब्लँकेट सर्वाधिक आवश्यक वस्तू आहेत. केवळ दान करण्यासाठी नोंदणीची गरज नाही.

औपचारिक माध्यमातून दान करायचे असल्यास, कुंभ एआय पोर्टलदेखील योगदान स्वीकारतो जे थेट संकलन केंद्रांकडे पाठवले जाते.`,
    sources: ["Volunteer Coordination Cell"],
  },
  "volunteer-id-required": {
    en: `**Yes, for a registered shift** — ID is required at the Seva Desk so you get an official badge and duty roster slot. For a walk-in donation or a single one-off task, no registration or ID is needed.

All volunteers get a basic safety briefing before their first shift regardless of how they signed up.`,
    mr: `**होय, नोंदणीकृत पाळीसाठी** — सेवा केंद्रावर ओळखपत्र आवश्यक आहे जेणेकरून आपल्याला अधिकृत बॅज व कर्तव्य वेळापत्रक स्लॉट मिळेल. प्रत्यक्ष उपस्थित राहून दान किंवा एक-वेळचे काम करण्यासाठी, नोंदणी किंवा ओळखपत्राची गरज नाही.

कसेही नोंदणी केली असली तरी, पहिल्या पाळीपूर्वी सर्व स्वयंसेवकांना मूलभूत सुरक्षा माहिती दिली जाते.`,
    sources: ["Seva Registration Portal"],
  },
  "atm-upi-stalls": {
    en: `✅ **Yes — UPI and QR-code payments are accepted at almost all shops, camps and stalls**, making it a reliable backup whenever a nearby ATM queue is long. This is one of the most consistent payment options across the entire ghat corridor.

💳 Fixed bank kiosks and mobile ATM vans remain available if you specifically need cash.`,
    mr: `✅ **होय — जवळपास सर्व दुकाने, शिबिरे व स्टॉलवर यूपीआय व क्यूआर-कोड पेमेंट स्वीकारले जाते**, त्यामुळे जवळचे एटीएम रांगेत असल्यास हा विश्वासार्ह पर्याय आहे. संपूर्ण घाट मार्गावरील सर्वात सातत्यपूर्ण पेमेंट पर्यायांपैकी हा एक आहे.

💳 विशेषतः रोख हवी असल्यास स्थिर बँक कक्ष व फिरत्या एटीएम व्हॅन उपलब्ध आहेत.`,
    sources: ["Digital Payment Helpdesk"],
  },
  "atm-nearest-to-gate": {
    en: `**2 fixed ATMs are near Ramkund Entry 2**, plus 1 mobile van rotating in that area. If you're at a different main entry, the mobile ATM van rotation covers Panchvati and Trimbakeshwar on a published 2-hour cycle — ask again and tell me your zone for the exact current location.

🏧 Nashik Road Stn also has 3 fixed ATMs inside the station complex.`,
    mr: `**रामकुंड प्रवेशद्वार 2 जवळ 2 स्थिर एटीएम** आहेत, तसेच त्या भागात फिरणारी 1 फिरती व्हॅन. आपण वेगळ्या मुख्य प्रवेशद्वारावर असल्यास, फिरती एटीएम व्हॅन फेरी पंचवटी व त्र्यंबकेश्वर प्रकाशित 2-तासांच्या चक्रानुसार कव्हर करते — आपला झोन सांगितल्यास नेमके सध्याचे स्थान सांगू शकतो.

🏧 नाशिक रोड स्थानकावरही स्थानक परिसरात 3 स्थिर एटीएम आहेत.`,
    sources: ["Bank Coordination Cell"],
  },
  "atm-cashback-stall": {
    en: `This isn't a standard feature at pilgrim stalls right now — most accept UPI/QR payments directly, which covers most needs without cash. For actual cash, the nearest fixed ATM or mobile ATM van is the reliable option.

💳 If a stall queue for cash comes up often in your zone, mention it — it's useful feedback for expanding the mobile ATM rotation.`,
    mr: `यात्रेकरू स्टॉलवर सध्या ही सुविधा प्रमाणित नाही — बहुतांश स्टॉल थेट यूपीआय/क्यूआर पेमेंट स्वीकारतात, ज्यामुळे रोखीशिवाय बहुतांश गरजा भागतात. प्रत्यक्ष रोख हवी असल्यास, जवळचे स्थिर एटीएम किंवा फिरती एटीएम व्हॅन हा विश्वासार्ह पर्याय आहे.

💳 आपल्या झोनमध्ये रोखीसाठी स्टॉल रांग वारंवार येत असल्यास, ते सांगा — फिरत्या एटीएम फेरीचा विस्तार करण्यासाठी हा उपयुक्त अभिप्राय आहे.`,
    sources: ["Digital Payment Helpdesk"],
  },
};

// Reverse index: follow-up id -> its 2 sibling follow-ups from the same
// parent topic, so a follow-up's own answer can still offer "you might also
// ask" chips instead of dead-ending the conversation.
const FOLLOWUP_SIBLINGS = {};
Object.values(RESPONSES).forEach((r) => {
  r.followups.forEach((f, idx) => {
    FOLLOWUP_SIBLINGS[f.id] = r.followups.filter((_, i) => i !== idx);
  });
});

const FALLBACK = {
  en: `I can help with the following right now:

**Crowd & Ghat Status** · **Transport & Parking** · **Lost & Found** · **Medical & Health Camps** · **Shahi Snan Schedule** · **Weather & River Level** · **Safety & Emergency Contacts** · **Accommodation** · **Toilets & Water** · **Volunteering & Donations** · **ATMs & Cash Points**

Try asking about one of these, or tap a quick topic on the left.`,
  mr: `मी सध्या खालील विषयांत मदत करू शकतो:

**गर्दी व घाट स्थिती** · **वाहतूक व पार्किंग** · **हरवले व सापडले** · **वैद्यकीय व आरोग्य शिबिरे** · **शाही स्नान वेळापत्रक** · **हवामान व नदी पातळी** · **सुरक्षा व आपत्कालीन संपर्क** · **निवास** · **शौचालय व पाणी** · **स्वयंसेवा व देणगी** · **एटीएम व रोकड केंद्रे**

यापैकी एखाद्या विषयाबद्दल विचारा, किंवा डावीकडील जलद विषयावर टॅप करा.`,
};

const KEYWORD_MAP = [
  { id: "crowd", re: /crowd|ghat|density|घाट|गर्दी|ramkund|panchvati|रामकुंड|पंचवटी/i },
  { id: "transport", re: /transport|bus|shuttle|parking|traffic|train|वाहतूक|पार्किंग|शटल|रस्ता/i },
  { id: "lostfound", re: /missing|lost|found|reunite|हरवल|सापडल|व्यक्ती/i },
  { id: "medical", re: /medical|health camp|hospital|ambulance|doctor|heat|आरोग्य|रुग्णालय|रुग्णवाहिका|उष्ण|वैद्यकीय|शिबिर/i },
  { id: "schedule", re: /shahi snan|snan|akhara|schedule|bathing|स्नान|आखाडा|वेळापत्रक/i },
  { id: "weather", re: /weather|rain|temperature|river|godavari|flood|हवामान|पाऊस|नदी|गोदावरी|(?<!गंगा)पूर/i },
  { id: "safety", re: /safety|emergency|sos|helpline|contact|police|सुरक्षा|आपत्कालीन|संपर्क|पोलीस/i },
  { id: "accommodation", re: /accommodation|stay|hotel|dharamshala|tent|camps?\b|निवास|राहण्याची|धर्मशाळा|कॅम्प/i },
  { id: "facilities", re: /toilet|water|facility|drinking|शौचालय|पाण|सुविधा/i },
  { id: "volunteer", re: /volunteer|donate|donation|seva|स्वयंसेवा|स्वयंसेवक|देणगी|दान|सेवा/i },
  { id: "atm", re: /atm|cash|money|upi|payment|withdraw|एटीएम|रोकड|पैसे|पेमेंट|यूपीआय|कॅश/i },
];

function getAIResponse(query) {
  const q = query.toLowerCase();
  for (const { id, re } of KEYWORD_MAP) {
    if (re.test(q)) return { id, ...RESPONSES[id] };
  }
  return null;
}

// ── Hand-rolled markdown-lite renderer — bold, numbered lists, a pipe-table,
// horizontal rules and emoji-status lines. No library, matches the reference
// pattern of parsing the canned response text line by line. ──
function renderInline(text) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) =>
    p.startsWith("**") && p.endsWith("**")
      ? <strong key={i} className="font-black text-slate-900">{p.slice(2, -2)}</strong>
      : <React.Fragment key={i}>{p}</React.Fragment>
  );
}

function MsgText({ text }) {
  const lines = text.split("\n");
  const blocks = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (!line.trim()) { i++; continue; }

    if (line.trim() === "---") {
      blocks.push(<hr key={i} className="my-3 border-slate-200" />);
      i++; continue;
    }

    if (line.trim().startsWith("|")) {
      const rows = [];
      while (i < lines.length && lines[i].trim().startsWith("|")) {
        rows.push(lines[i].trim().replace(/^\||\|$/g, "").split("|").map((c) => c.trim()));
        i++;
      }
      blocks.push(
        <div key={i} className="overflow-x-auto my-2.5 rounded-lg border border-slate-200">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                {rows[0].map((h, hi) => (
                  <th key={hi} className="text-left px-2.5 py-1.5 font-black text-slate-500 uppercase tracking-wide">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.slice(1).map((r, ri) => (
                <tr key={ri}>
                  {r.map((c, ci) => <td key={ci} className="px-2.5 py-1.5 text-slate-700">{renderInline(c)}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
      continue;
    }

    if (/^\d+\.\s/.test(line.trim())) {
      const items = [];
      while (i < lines.length && /^\d+\.\s/.test(lines[i].trim())) {
        items.push(lines[i].trim().replace(/^\d+\.\s/, ""));
        i++;
      }
      blocks.push(
        <ol key={i} className="my-2 space-y-1.5">
          {items.map((it, ii) => (
            <li key={ii} className="flex items-start gap-2 text-sm text-slate-700">
              <span className="w-4 h-4 rounded-full bg-saffron-light text-saffron-dark text-[9px] font-black flex items-center justify-center flex-shrink-0 mt-0.5">{ii + 1}</span>
              <span>{renderInline(it)}</span>
            </li>
          ))}
        </ol>
      );
      continue;
    }

    if (line.trim().startsWith("-") || line.trim().startsWith("•")) {
      const items = [];
      while (i < lines.length && (lines[i].trim().startsWith("-") || lines[i].trim().startsWith("•"))) {
        items.push(lines[i].trim().replace(/^[-•]\s*/, ""));
        i++;
      }
      blocks.push(
        <ul key={i} className="my-2 space-y-1">
          {items.map((it, ii) => (
            <li key={ii} className="flex items-start gap-2 text-sm text-slate-700">
              <span className="w-1.5 h-1.5 rounded-full bg-saffron mt-1.5 flex-shrink-0" />
              <span>{renderInline(it)}</span>
            </li>
          ))}
        </ul>
      );
      continue;
    }

    if (/^[🔴🟡🟢✅⚠️🚫🏕️🚰☀️📈]/.test(line.trim())) {
      blocks.push(
        <p key={i} className="my-2 text-sm font-semibold text-slate-800 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
          {renderInline(line.trim())}
        </p>
      );
      i++; continue;
    }

    if (/^\*\*[^*]+\*\*$/.test(line.trim())) {
      blocks.push(
        <div key={i} className="mt-3 mb-1.5 text-sm font-black text-saffron-dark">
          {line.trim().slice(2, -2)}
        </div>
      );
      i++; continue;
    }

    blocks.push(<p key={i} className="text-sm text-slate-700 leading-relaxed my-1.5">{renderInline(line)}</p>);
    i++;
  }
  return <div>{blocks}</div>;
}

function TypingIndicator() {
  return (
    <div className="flex items-center gap-1.5 px-4 py-3">
      {[0, 1, 2].map((d) => (
        <span
          key={d}
          className="w-2 h-2 rounded-full bg-saffron/60 animate-bounce"
          style={{ animationDelay: `${d * 0.15}s` }}
        />
      ))}
    </div>
  );
}

let msgId = 0;
const nextId = () => `m${++msgId}`;

export default function KumbhSahayak() {
  const { lang } = usePreferences();
  const c = COPY[lang];

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [thinking, setThinking] = useState(false);
  const [leftTab, setLeftTab] = useState("topics");
  const [activeZone, setActiveZone] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [queryCount, setQueryCount] = useState(0);
  const [demoRunning, setDemoRunning] = useState(false);
  const [demoStep, setDemoStep] = useState(0);
  const listRef = useRef(null);

  const welcome = () => ({
    id: nextId(),
    role: "bot",
    text: lang === "mr"
      ? "नमस्कार! मी **कुंभ साथी** आहे — गर्दीची स्थिती, वाहतूक, वैद्यकीय शिबिरे, हरवलेल्या व्यक्ती आणि बरेच काही याबद्दल विचारा. डावीकडील जलद विषयावर टॅप करा किंवा खाली टाइप करा."
      : "Welcome! I'm **Kumbh Sathi** — ask me about crowd status, transport, medical camps, lost & found and more. Tap a quick topic on the left or type below.",
  });

  useEffect(() => {
    setMessages([welcome()]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, thinking]);

  // Scripted Collector-demo playback — one auto-sent question at a time.
  // demoStep is "index of the next topic to send"; each firing bumps it, so
  // this effect naturally re-triggers itself once the bot's "thinking" delay
  // clears, pausing between answers so the audience has time to read one
  // before the next question appears. Stopping is just setDemoRunning(false)
  // — the effect's own cleanup cancels whatever send is still pending.
  useEffect(() => {
    if (!demoRunning || thinking) return;
    if (demoStep >= DEMO_SCRIPT.length) { setDemoRunning(false); return; }
    const timer = setTimeout(() => {
      const topic = TOPICS.find((t) => t.id === DEMO_SCRIPT[demoStep]);
      if (topic) sendMessage(topic.prompt[lang]);
      setDemoStep((s) => s + 1);
    }, demoStep === 0 ? 600 : 3200);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [demoRunning, thinking, demoStep]);

  // A language switch mid-demo would mix English and Marathi turns in the
  // same transcript — stop rather than let that happen.
  useEffect(() => {
    if (demoRunning) setDemoRunning(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);

  const startDemo = () => {
    setMessages([welcome()]);
    setActiveZone(null);
    setDemoStep(0);
    setDemoRunning(true);
  };

  const stopDemo = () => setDemoRunning(false);

  const sendMessage = (text) => {
    const q = text.trim();
    if (!q || thinking) return;
    setMessages((m) => [...m, { id: nextId(), role: "user", text: q }]);
    setInput("");
    setThinking(true);
    setQueryCount((n) => n + 1);
    const delay = 900 + Math.random() * 900;
    setTimeout(() => {
      const match = getAIResponse(q);
      const body = match ? match[lang] : FALLBACK[lang];
      const sources = match ? match.sources : null;
      const followups = match ? match.followups : null;
      setMessages((m) => [...m, { id: nextId(), role: "bot", text: body, sources, followups }]);
      setThinking(false);
    }, delay);
  };

  // Follow-up chips are answered directly by id (FOLLOWUP_RESPONSES) instead
  // of going back through the keyword router — that's what makes each one
  // give its own specific answer rather than repeating the parent topic's
  // full overview. Sibling follow-ups from FOLLOWUP_SIBLINGS keep the
  // conversation exploring sideways instead of dead-ending.
  const sendFollowup = (followup) => {
    if (thinking) return;
    setMessages((m) => [...m, { id: nextId(), role: "user", text: followup[lang] }]);
    setThinking(true);
    setQueryCount((n) => n + 1);
    const delay = 900 + Math.random() * 900;
    setTimeout(() => {
      const match = FOLLOWUP_RESPONSES[followup.id];
      const body = match ? match[lang] : FALLBACK[lang];
      const sources = match ? match.sources : null;
      const followups = FOLLOWUP_SIBLINGS[followup.id] || null;
      setMessages((m) => [...m, { id: nextId(), role: "bot", text: body, sources, followups }]);
      setThinking(false);
    }, delay);
  };

  const handleNewChat = () => {
    setDemoRunning(false);
    setMessages([welcome()]);
    setActiveZone(null);
  };

  const copyMessage = (id, text) => {
    navigator.clipboard?.writeText(text.replace(/\*\*/g, "")).catch(() => {});
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 1500);
  };

  return (
    <div className="space-y-6">
      {/* ── Hero ── */}
      <div className="relative rounded-3xl gov-gradient-hero overflow-hidden p-6 md:p-8">
        {/* Real pilgrims outside the Trimbakeshwar gate — the people this
            assistant exists to help, rather than an abstract pattern.
            Already shipped for the landing page, so it costs no extra
            download and its licence is already tracked.

            This hero runs WHITE text, so the veil here is dark, not white
            like the dashboard panel: white type needs darkness behind it.
            The photo sits at full strength on the right, where only the stat
            tiles are, and is held back on the left under the heading. */}
        <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
          <img
            src="/images/trimbakeshwar-pilgrims.jpg"
            alt=""
            loading="lazy"
            className="absolute inset-0 h-full w-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-saffron-dark/65 via-saffron-dark/30 to-saffron-dark/5" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent" />
        </div>

        {/* Licence-required credit (CC BY-SA 4.0, cropped). Small, but present
            wherever the photo appears — see public/images/README.md. */}
        <a
          href="https://commons.wikimedia.org/wiki/File:Kumbhmela_Nashik_2015_-_Trimbakeshwar_temple_entrance.JPG"
          target="_blank"
          rel="noopener noreferrer"
          className="absolute bottom-1.5 right-3 z-10 text-[9px] leading-none text-white/55 hover:text-white/85 hover:underline underline-offset-2"
        >
          Prabirghose · CC BY-SA 4.0 · cropped
        </a>

        <div className="absolute -top-16 -right-10 w-64 h-64 rounded-full bg-white/10 pointer-events-none" />
        <div className="relative flex flex-col lg:flex-row lg:items-end justify-between gap-6">
          <div className="max-w-xl">
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-black/25 border border-white/30 rounded-full mb-4">
              <Sparkles size={11} className="text-white" />
              <span className="text-[10px] font-black text-white tracking-wider uppercase [text-shadow:0_1px_2px_rgba(0,0,0,0.7)]">{c.badge}</span>
            </div>
            <h1 className="text-2xl md:text-3xl font-black text-white tracking-tight flex items-center gap-2.5 [text-shadow:0_1px_3px_rgba(0,0,0,0.8),0_3px_12px_rgba(0,0,0,0.5)]">
              <Bot size={26} className="text-white flex-shrink-0" /> {c.title}
            </h1>
            <p className="mt-2 text-sm text-white leading-relaxed [text-shadow:0_1px_3px_rgba(0,0,0,0.8),0_3px_12px_rgba(0,0,0,0.5)]">{c.subtitle}</p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 flex-shrink-0">
            {[
              [queryCount, c.statQueries],
              [TOPICS.length, c.statTopics],
              [ZONES.length, c.statZones],
              [2, c.statLangs],
            ].map(([val, label]) => (
              <div key={label} className="rounded-xl bg-black/40 border border-white/35 px-3.5 py-2.5 min-w-[92px] backdrop-blur-sm">
                <div className="text-xl font-black text-white leading-none">{val}</div>
                <div className="text-[10px] font-semibold text-white/75 mt-1">{label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Body: topics/zones panel + chat ── */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-5">
        {/* Left panel — inert while the scripted demo is driving the chat,
            so a stray click can't fire a question out of sequence. */}
        <div className={`lg:col-span-1 space-y-4 transition-opacity ${demoRunning ? "opacity-50 pointer-events-none" : ""}`}>
          <div className="rounded-2xl border border-slate-200 bg-white p-1.5 flex gap-1">
            {[["topics", c.tabTopics], ["zones", c.tabZones]].map(([key, label]) => (
              <button
                key={key}
                onClick={() => setLeftTab(key)}
                className={`flex-1 text-xs font-bold px-3 py-2 rounded-xl transition-colors ${
                  leftTab === key ? "bg-saffron text-white" : "text-slate-500 hover:bg-slate-50"
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {leftTab === "topics" ? (
            <div className="grid grid-cols-2 gap-2.5">
              {TOPICS.map((t, i) => {
                const clr = googleColor(i);
                const Icon = t.icon;
                return (
                  <motion.button
                    key={t.id}
                    whileHover={{ y: -2 }}
                    onClick={() => sendMessage(t.prompt[lang])}
                    className="text-left rounded-xl p-3 border transition-shadow hover:shadow-sm"
                    style={{ background: clr.bgLight, borderColor: clr.border }}
                  >
                    <div className="w-8 h-8 rounded-lg bg-white/70 flex items-center justify-center mb-2">
                      <Icon size={15} style={{ color: clr.text }} />
                    </div>
                    <div className="text-[11px] font-bold text-slate-700 leading-tight">{t[lang]}</div>
                  </motion.button>
                );
              })}
            </div>
          ) : (
            <div className="rounded-2xl border border-slate-200 bg-white p-2 space-y-1">
              {ZONES.map((z, i) => {
                const clr = googleColor(i);
                return (
                  <button
                    key={z}
                    onClick={() => { setActiveZone(z); sendMessage(c.zoneBriefing(lang === "mr" ? ZONES_MR[z] : z)); }}
                    className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-left transition-colors ${
                      activeZone === z ? "bg-saffron-light" : "hover:bg-slate-50"
                    }`}
                  >
                    <MapPin size={14} style={{ color: clr.text }} className="flex-shrink-0" />
                    <span className="text-sm font-semibold text-slate-700">{lang === "mr" ? ZONES_MR[z] : z}</span>
                  </button>
                );
              })}
            </div>
          )}

          <div className="rounded-2xl bg-amber-50 border border-amber-200 p-3.5 flex items-start gap-2.5">
            <Info size={15} className="text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-[11px] text-amber-800 leading-relaxed">{c.disclaimer}</p>
          </div>
        </div>

        {/* Chat panel */}
        <div className="lg:col-span-3 rounded-2xl border border-slate-200 bg-white flex flex-col h-[640px]">
          {/* header */}
          <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-100 flex-shrink-0">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-xl gov-gradient flex items-center justify-center relative flex-shrink-0">
                <Bot size={17} className="text-white" />
                <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-emerald-400 border-2 border-white live-dot" />
              </div>
              <div>
                <div className="text-sm font-black text-slate-900 leading-none">{c.chatHeader}</div>
                <div className="text-[11px] text-slate-400 mt-0.5">{c.chatSub}</div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {demoRunning ? (
                <span className="hidden sm:inline-flex items-center gap-1.5 text-[10px] font-black text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 live-dot" />
                  {c.demoLive} · {c.demoProgress(Math.min(demoStep, DEMO_SCRIPT.length), DEMO_SCRIPT.length)}
                </span>
              ) : (
                <span className="hidden sm:inline text-[10px] font-semibold text-slate-400">{c.poweredBy}</span>
              )}
              <button
                onClick={demoRunning ? stopDemo : startDemo}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold border transition-all ${
                  demoRunning
                    ? "text-red-600 bg-red-50 border-red-200 hover:bg-red-100"
                    : "text-blue-700 bg-blue-50 border-blue-200 hover:bg-blue-100"
                }`}
              >
                {demoRunning ? <StopCircle size={12} /> : <PlayCircle size={12} />}
                {demoRunning ? c.stopDemo : c.runDemo}
              </button>
              <button
                onClick={handleNewChat}
                disabled={demoRunning}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold text-saffron-dark bg-saffron-light border border-saffron/30 hover:brightness-95 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
              >
                <RotateCcw size={12} /> {c.newChat}
              </button>
            </div>
          </div>

          {/* messages */}
          <div ref={listRef} className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
            {messages.map((m) => (
              <motion.div
                key={m.id}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                className={`flex gap-2.5 ${m.role === "user" ? "justify-end" : "justify-start"}`}
              >
                {m.role === "bot" && (
                  <div className="w-7 h-7 rounded-lg gov-gradient flex items-center justify-center flex-shrink-0 mt-1">
                    <Bot size={13} className="text-white" />
                  </div>
                )}
                <div className={`max-w-[80%] ${m.role === "user" ? "order-1" : ""}`}>
                  {m.role === "user" ? (
                    <div className="bg-saffron text-white rounded-2xl rounded-tr-sm px-4 py-2.5 text-sm font-medium">
                      {m.text}
                    </div>
                  ) : (
                    <div className="bg-slate-50 border border-slate-200 rounded-2xl rounded-tl-sm px-4 py-3">
                      <MsgText text={m.text} />
                      {m.sources && (
                        <div className="mt-2.5 pt-2.5 border-t border-slate-200 flex flex-wrap items-center gap-1.5">
                          <span className="text-[10px] font-black text-slate-400 uppercase tracking-wide">{c.sources}:</span>
                          {m.sources.map((s) => (
                            <span key={s} className="text-[10px] font-semibold text-slate-500 bg-white border border-slate-200 rounded-full px-2 py-0.5">{s}</span>
                          ))}
                        </div>
                      )}
                      {m.followups && (
                        <div className={`mt-2.5 pt-2.5 border-t border-slate-200 transition-opacity ${demoRunning ? "opacity-50 pointer-events-none" : ""}`}>
                          <div className="text-[10px] font-black text-slate-400 uppercase tracking-wide mb-1.5">{c.askFollowup}</div>
                          <div className="flex flex-wrap gap-1.5">
                            {m.followups.map((f) => (
                              <button
                                key={f.id}
                                onClick={() => sendFollowup(f)}
                                disabled={demoRunning}
                                className="text-[11px] font-semibold text-saffron-dark bg-saffron-light border border-saffron/30 rounded-full px-2.5 py-1 hover:brightness-95 transition-all"
                              >
                                {f[lang]}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                      <button
                        onClick={() => copyMessage(m.id, m.text)}
                        className="mt-2 inline-flex items-center gap-1 text-[10px] font-semibold text-slate-400 hover:text-slate-600 transition-colors"
                      >
                        {copiedId === m.id ? <Check size={11} /> : <Clipboard size={11} />}
                        {copiedId === m.id ? c.copied : c.copy}
                      </button>
                    </div>
                  )}
                </div>
                {m.role === "user" && (
                  <div className="w-7 h-7 rounded-lg bg-slate-200 flex items-center justify-center flex-shrink-0 mt-1">
                    <User size={13} className="text-slate-500" />
                  </div>
                )}
              </motion.div>
            ))}

            {thinking && (
              <div className="flex gap-2.5">
                <div className="w-7 h-7 rounded-lg gov-gradient flex items-center justify-center flex-shrink-0 mt-1">
                  <Bot size={13} className="text-white" />
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-2xl rounded-tl-sm">
                  <TypingIndicator />
                </div>
              </div>
            )}
          </div>

          {/* input */}
          <div className="border-t border-slate-100 p-3.5 flex-shrink-0">
            <form
              onSubmit={(e) => { e.preventDefault(); sendMessage(input); }}
              className="flex items-end gap-2.5"
            >
              <div className="flex-1 relative">
                <Search size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-300 pointer-events-none" />
                <textarea
                  rows={1}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      sendMessage(input);
                    }
                  }}
                  placeholder={demoRunning ? c.demoLive : c.inputPlaceholder}
                  maxLength={500}
                  disabled={demoRunning}
                  className="w-full resize-none pl-9 pr-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-800 placeholder-slate-400 outline-none focus:border-saffron focus:ring-2 focus:ring-saffron/15 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                />
              </div>
              <button
                type="submit"
                disabled={!input.trim() || thinking || demoRunning}
                className="w-10 h-10 rounded-xl bg-saffron text-white flex items-center justify-center flex-shrink-0 hover:brightness-95 disabled:opacity-40 disabled:cursor-not-allowed transition-all active:scale-95"
              >
                <Send size={16} />
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
